import React, { useState, useEffect } from 'react';
import { 
  Activity, 
  Camera, 
  Heart, 
  MessageCircle, 
  Search, 
  RefreshCw,
  Edit,
  Trash2,
  Eye,
  User,
  Calendar,
  Clock,
  AlertCircle,
  X,
  BarChart,
  LogOut,
  AlertTriangle
} from 'lucide-react';
import { API_BASE_URL, API_ENDPOINTS, getAuthHeaders } from '../config/api.ts';
import ProtectedData from '../components/ProtectedData.tsx';
import FormSelect from '../components/FormSelect.tsx';

interface DogStatusUpdate {
  _id: string;
  dogId: {
    _id: string;
    name: string;
    breed: string;
    profileImage?: string;
  };
  ownerId: {
    _id: string;
    name: string;
    email: string;
  };
  branchId: {
    _id: string;
    name: string;
    contact: {
      phone: string;
    };
  };
  staffId: {
    _id: string;
    name: string;
    role: string;
  };
  type: 'activity' | 'photo' | 'health' | 'general' | 'checkout';
  message: string;
  status: string;
  description: string;
  photoUrl?: string;
  photoCaption?: string;
  activityDetails?: {
    activity?: string;
    duration?: number;
    location?: string;
    mood?: string;
    energyLevel?: number;
  };
  healthDetails?: {
    temperature?: number;
    weight?: number;
    notes?: string;
    nextCheckup?: string;
  };
  isRead: boolean;
  readAt?: string;
  createdAt: string;
  updatedAt: string;
}

interface Stats {
  total: number;
  activity: number;
  photo: number;
  health: number;
  general: number;
  checkout: number;
  unread: number;
}

interface Pagination {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
}

const TYPE_ICONS = {
  activity: { icon: Activity, color: 'bg-blue-100 text-blue-800', label: 'Aktivite' },
  photo: { icon: Camera, color: 'bg-green-100 text-green-800', label: 'Fotoğraf' },
  health: { icon: Heart, color: 'bg-red-100 text-red-800', label: 'Sağlık' },
  general: { icon: MessageCircle, color: 'bg-gray-100 text-gray-800', label: 'Genel' },
  checkout: { icon: LogOut, color: 'bg-purple-100 text-purple-800', label: 'Çıkış' }
};

// Mood mapping - İngilizce'den Türkçe'ye çeviri
const MOOD_MAPPING: { [key: string]: string } = {
  'happy': 'Mutlu',
  'calm': 'Sakin', 
  'excited': 'Heyecanlı',
  'tired': 'Yorgun',
  'anxious': 'Endişeli',
  'playful': 'Oyunbaz',
  'relaxed': 'Rahat',
  'energetic': 'Enerjik',
  'sleepy': 'Uykulu',
  'alert': 'Dikkatli',
  'content': 'Memnun',
  'stressed': 'Stresli',
  'friendly': 'Dostane',
  'shy': 'Utangaç',
  'aggressive': 'Agresif',
  'neutral': 'Nötr',
  'other': 'Diğer'
};

// Activity mapping - İngilizce'den Türkçe'ye çeviri  
const ACTIVITY_MAPPING: { [key: string]: string } = {
  'eating': 'Yemek',
  'playing': 'Oyun',
  'sleeping': 'Uyku',
  'walking': 'Yürüyüş',
  'training': 'Eğitim',
  'resting': 'Dinlenme',
  'socializing': 'Sosyalleşme',
  'grooming': 'Temizlik',
  'exercise': 'Egzersiz',
  'exploring': 'Keşif',
  'other': 'Diğer'
};

const KopekDurumGuncellemeleri: React.FC = () => {
  const [allUpdates, setAllUpdates] = useState<DogStatusUpdate[]>([]); // CACHE - Tüm veri burada
  const [stats, setStats] = useState<Stats>({
    total: 0,
    activity: 0,
    photo: 0,
    health: 0,
    general: 0,
    checkout: 0,
    unread: 0
  });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [selectedUpdate, setSelectedUpdate] = useState<DogStatusUpdate | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [editingUpdate, setEditingUpdate] = useState<DogStatusUpdate | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingUpdateId, setDeletingUpdateId] = useState<string | null>(null);
  const [newPhoto, setNewPhoto] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [removeCurrentPhoto, setRemoveCurrentPhoto] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // TÜM VERİYİ BİR KERE ÇEK VE CACHE'E KOY
  const fetchUpdates = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        throw new Error('Admin token bulunamadı');
      }

      const headers = getAuthHeaders(token);
      
      // TÜM verileri bir seferde çek (limit 1000)
      const response = await fetch(
        `${API_BASE_URL}${API_ENDPOINTS.DOG_STATUS_BRANCH}?page=1&limit=1000`,
        {
          method: 'GET',
          headers,
        }
      );

      const data = await response.json();
      
      if (data.success) {
        const fetchedUpdates = data.data || [];
        setAllUpdates(fetchedUpdates); // CACHE'e kaydet
        
        // Stats'ı hesapla
        const calculatedStats = {
          total: fetchedUpdates.length,
          activity: fetchedUpdates.filter((u: DogStatusUpdate) => u.type === 'activity').length,
          photo: fetchedUpdates.filter((u: DogStatusUpdate) => u.type === 'photo').length,
          health: fetchedUpdates.filter((u: DogStatusUpdate) => u.type === 'health').length,
          general: fetchedUpdates.filter((u: DogStatusUpdate) => u.type === 'general').length,
          checkout: fetchedUpdates.filter((u: DogStatusUpdate) => u.type === 'checkout').length,
          unread: fetchedUpdates.filter((u: DogStatusUpdate) => !u.isRead).length
        };
        setStats(calculatedStats);
      } else {
        throw new Error(data.message || 'Durum güncellemeleri getirilemedi');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Bilinmeyen hata');
      setAllUpdates([]);
    } finally {
      setLoading(false);
    }
  };

  // Sadece ilk yüklemede çalışsın
  useEffect(() => {
    fetchUpdates();
  }, []);

  // FRONTEND FİLTRELEME (CACHE'ten)
  const filteredUpdates = allUpdates.filter(update => {
    // Tip filtresi
    const matchesType = filterType === 'all' || update.type === filterType;
    
    // Arama filtresi
    const matchesSearch = 
      !searchTerm ||
      (update.dogId?.name && update.dogId.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (update.ownerId?.name && update.ownerId.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (update.staffId?.name && update.staffId.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (update.status && update.status.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (update.description && update.description.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return matchesType && matchesSearch;
  });

  // Frontend pagination
  const totalPages = Math.ceil(filteredUpdates.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredUpdates.slice(indexOfFirstItem, indexOfLastItem);

  const pagination = {
    currentPage,
    totalPages,
    totalItems: filteredUpdates.length,
    itemsPerPage
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('tr-TR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleCardClick = (type: string) => {
    setFilterType(type); // Sadece state değiştir, API'ye gitme!
    setCurrentPage(1); // Sayfa 1'e dön
  };

  const handleUpdateClick = (update: DogStatusUpdate) => {
    setSelectedUpdate(update);
    setShowDetails(true);
  };

  const handleEdit = (update: DogStatusUpdate) => {
    setEditingUpdate(update);
    setNewPhoto(null);
    setPhotoPreview(null);
    setRemoveCurrentPhoto(false);
    setShowEditModal(true);
  };

  const handlePhotoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setNewPhoto(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setPhotoPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeNewPhoto = () => {
    setNewPhoto(null);
    setPhotoPreview(null);
  };

  const toggleRemoveCurrentPhoto = () => {
    setRemoveCurrentPhoto(!removeCurrentPhoto);
    if (!removeCurrentPhoto) {
      // Mevcut fotoğrafı kaldırırken yeni fotoğrafı da temizle
      setNewPhoto(null);
      setPhotoPreview(null);
    }
  };

  const handleSaveEdit = async () => {
    if (!editingUpdate || isSaving) return;

    setIsSaving(true);
    setError(null);

    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        setError('Admin token bulunamadı');
        setIsSaving(false);
        return;
      }

      let response;
      
      if ((newPhoto || removeCurrentPhoto) && editingUpdate?.type === 'photo') {
        // Fotoğraf ile birlikte güncelleme
        const formData = new FormData();
        formData.append('status', editingUpdate.status);
        formData.append('description', editingUpdate.description);
        formData.append('photoCaption', editingUpdate.photoCaption || '');
        formData.append('removeCurrentPhoto', removeCurrentPhoto.toString());
        
        // Activity ve health details'i de ekle
        if (editingUpdate.activityDetails) {
          formData.append('activityDetails', JSON.stringify(editingUpdate.activityDetails));
        }
        if (editingUpdate.healthDetails) {
          formData.append('healthDetails', JSON.stringify(editingUpdate.healthDetails));
        }
        
        if (newPhoto) {
          formData.append('photo', newPhoto);
        }
        
        response = await fetch(
          `${API_BASE_URL}${API_ENDPOINTS.DOG_STATUS_ADMIN_UPDATE.replace('{updateId}', editingUpdate._id)}`,
          {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'X-API-Key': process.env.REACT_APP_API_KEY || (() => {
                console.error('REACT_APP_API_KEY environment variable is not set!');
                throw new Error('API Key is required. Please set REACT_APP_API_KEY in your .env file.');
              })()
            },
            body: formData
          }
        );
      } else {
        // Normal güncelleme
        const headers = getAuthHeaders(token);
        
        response = await fetch(
          `${API_BASE_URL}${API_ENDPOINTS.DOG_STATUS_ADMIN_UPDATE.replace('{updateId}', editingUpdate._id)}`,
          {
            method: 'PUT',
            headers,
            body: JSON.stringify({
              status: editingUpdate.status,
              description: editingUpdate.description,
              photoCaption: editingUpdate.photoCaption,
              activityDetails: editingUpdate.activityDetails,
              healthDetails: editingUpdate.healthDetails
            })
          }
        );
      }

      const data = await response.json();
      
      if (data.success) {
        setShowEditModal(false);
        setEditingUpdate(null);
        setNewPhoto(null);
        setPhotoPreview(null);
        setRemoveCurrentPhoto(false);
        setIsSaving(false);
        fetchUpdates(); // Tüm veriyi yeniden çek
      } else {
        setError(data.message || 'Düzenleme işlemi başarısız');
        setIsSaving(false);
      }
    } catch (err) {
      setError('Düzenleme işlemi sırasında hata oluştu');
      setIsSaving(false);
    }
  };

  const handleDelete = async (updateId: string) => {
    // Open delete confirmation modal instead of window.confirm
    setDeletingUpdateId(updateId);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deletingUpdateId) return;

    const updateId = deletingUpdateId;
    setShowDeleteModal(false);
    setDeletingUpdateId(null);

    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        setError('Admin token bulunamadı');
        return;
      }

      const headers = getAuthHeaders(token);
      
      const response = await fetch(
        `${API_BASE_URL}${API_ENDPOINTS.DOG_STATUS_ADMIN_DELETE.replace('{updateId}', updateId)}`,
        {
          method: 'DELETE',
          headers,
        }
      );

      const data = await response.json();
      
      if (data.success) {
        fetchUpdates(); // Tüm veriyi yeniden çek
      } else {
        setError(data.message || 'Silme işlemi başarısız');
      }
    } catch (err) {
      setError('Silme işlemi sırasında hata oluştu');
    }
  };

  const handleRefresh = () => {
    fetchUpdates(); // API'den tüm veriyi yeniden çek
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage); // Sadece sayfa değiştir, API'ye gitme!
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-600 mx-auto"></div>
              <p className="text-gray-600 mt-4">Durum güncellemeleri yükleniyor...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-stone-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-orange-500 to-amber-500 bg-clip-text text-transparent">🐾 Köpek Durum Güncellemeleri</h1>
              <p className="text-gray-600 mt-2 font-medium">Staff'ların attığı durum güncellemelerini yönetin</p>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleRefresh}
                className="flex items-center space-x-2 bg-gradient-to-r from-orange-500 to-amber-600 text-white px-5 py-2.5 rounded-xl hover:from-orange-600 hover:to-amber-700 transition-all shadow-md hover:shadow-lg"
              >
                <RefreshCw size={18} />
                <span className="font-medium">Yenile</span>
              </button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
            <div 
              className={`bg-white rounded-xl shadow-md border-2 p-4 cursor-pointer transition-all duration-300 hover:shadow-lg hover:scale-[1.02] ${
                filterType === 'all' ? 'border-orange-300 bg-gradient-to-br from-orange-50 to-amber-50' : 'border-gray-200 hover:border-orange-200'
              }`}
              onClick={() => handleCardClick('all')}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Toplam</p>
                  <p className="text-2xl font-black text-gray-900 mt-1">{stats.total}</p>
                </div>
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                  filterType === 'all' ? 'bg-gradient-to-br from-orange-400 to-amber-500 shadow-md' : 'bg-gray-100'
                }`}>
                  <BarChart size={20} className={filterType === 'all' ? 'text-white' : 'text-gray-600'} />
                </div>
              </div>
            </div>

            {Object.entries(TYPE_ICONS).map(([type, config]) => {
              const Icon = config.icon;
              const count = stats[type as keyof Stats] || 0;
              const isActive = filterType === type;
              return (
                <div 
                  key={type}
                  className={`bg-white rounded-xl shadow-md border-2 p-4 cursor-pointer transition-all duration-300 hover:shadow-lg hover:scale-[1.02] ${
                    isActive ? 'border-orange-300 bg-gradient-to-br from-orange-50 to-amber-50' : 'border-gray-200 hover:border-orange-200'
                  }`}
                  onClick={() => handleCardClick(type)}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">{config.label}</p>
                      <p className="text-2xl font-black text-gray-900 mt-1">{count}</p>
                    </div>
                    <Icon size={20} className={isActive ? 'text-orange-600' : 'text-gray-600'} />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Search Bar */}
          <div className="bg-white rounded-xl shadow-md border-2 border-gray-200 p-5">
            <div className="flex items-center space-x-4">
              <div className="flex-1 relative">
                <div className="absolute left-4 top-1/2 transform -translate-y-1/2 w-9 h-9 bg-gray-100 rounded-lg flex items-center justify-center">
                  <Search className="text-gray-600" size={18} />
                </div>
                <input
                  type="text"
                  placeholder="Köpek adı, sahip adı, personel adı veya durum ara..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-14 pr-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-400 focus:border-orange-400 transition-all bg-gray-50 focus:bg-white font-medium"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-gradient-to-r from-red-50 to-rose-50 border-2 border-red-200 rounded-xl p-5 mb-6 shadow-sm">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center shadow-sm">
                <AlertCircle className="text-red-600" size={22} />
              </div>
              <div>
                <p className="font-bold text-red-900">Hata Oluştu</p>
                <span className="text-red-700 text-sm font-medium">{error}</span>
              </div>
            </div>
          </div>
        )}

        {/* Updates List */}
        {filteredUpdates.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-16 text-center">
            <div className="w-20 h-20 bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-inner">
              <Activity className="text-gray-400" size={40} />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Durum Güncellemesi Bulunamadı</h3>
            <p className="text-gray-600">
              {searchTerm || filterType !== 'all' ? 'Arama/filtre kriterlerinize uygun güncelleme bulunamadı.' : 'Henüz durum güncellemesi bulunmuyor.'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {currentItems.map((update) => {
              const typeConfig = TYPE_ICONS[update.type];
              const TypeIcon = typeConfig.icon;
              
              return (
                <div
                  key={update._id}
                  className="bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden hover:shadow-xl transition-all duration-300 hover:scale-[1.01]"
                >
                  <div className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-4 flex-1">
                        {/* Dog Image */}
                        <div className="w-20 h-20 bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl flex items-center justify-center flex-shrink-0 ring-2 ring-gray-300 ring-offset-2 shadow-sm">
                          {update.dogId?.profileImage ? (
                            <img 
                              src={update.dogId?.profileImage} 
                              alt={update.dogId?.name || 'Köpek'}
                              className="w-20 h-20 rounded-2xl object-cover"
                            />
                          ) : (
                            <Activity size={28} className="text-gray-400" />
                          )}
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2 mb-3 flex-wrap gap-2">
                            <h3 className="text-xl font-bold text-gray-900">
                              {update.dogId?.name || 'Bilinmeyen Köpek'}
                            </h3>
                            <span className={`inline-flex items-center px-3 py-1 text-xs font-bold rounded-full ${typeConfig.color} shadow-sm`}>
                              <TypeIcon size={14} className="mr-1.5" />
                              {typeConfig.label}
                            </span>
                            {update.photoUrl && update.type !== 'photo' && (
                              <span className="inline-flex items-center px-3 py-1 text-xs font-bold rounded-full bg-emerald-100 text-emerald-800 shadow-sm">
                                <Camera size={14} className="mr-1.5" />
                                Fotoğraf
                              </span>
                            )}
                          </div>

                          <p className="text-gray-700 mb-4 line-clamp-2 leading-relaxed font-medium">{update.description}</p>

                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                            <div className="flex items-center space-x-2 bg-gray-50 px-3 py-2 rounded-lg">
                              <User size={16} className="text-indigo-600" />
                              <span className="truncate font-medium text-gray-700">{update.ownerId?.name || 'Bilinmeyen'}</span>
                            </div>
                            <div className="flex items-center space-x-2 bg-gray-50 px-3 py-2 rounded-lg">
                              <Activity size={16} className="text-purple-600" />
                              <span className="truncate font-medium text-gray-700">{update.staffId?.name || 'Bilinmeyen'}</span>
                            </div>
                            <div className="flex items-center space-x-2 bg-gray-50 px-3 py-2 rounded-lg">
                              <Clock size={16} className="text-blue-600" />
                              <span className="truncate font-medium text-gray-700">{formatTime(update.createdAt)}</span>
                            </div>
                            <div className="flex items-center space-x-2 bg-gray-50 px-3 py-2 rounded-lg">
                              <Calendar size={16} className="text-rose-600" />
                              <span className="truncate font-medium text-gray-700">
                                {new Date(update.createdAt).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' })}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center space-x-2 ml-4">
                        <button
                          onClick={() => handleUpdateClick(update)}
                          className="p-2.5 text-gray-400 hover:text-orange-600 hover:bg-orange-50 rounded-xl transition-all hover:scale-110"
                          title="Detayları Görüntüle"
                        >
                          <Eye size={20} />
                        </button>
                        <button
                          onClick={() => handleEdit(update)}
                          className="p-2.5 text-gray-400 hover:text-orange-600 hover:bg-orange-50 rounded-xl transition-all hover:scale-110"
                          title="Düzenle"
                        >
                          <Edit size={20} />
                        </button>
                        <button
                          onClick={() => handleDelete(update._id)}
                          className="p-2.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all hover:scale-110"
                          title="Sil"
                        >
                          <Trash2 size={20} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-center space-x-3 mt-8">
            <button
              onClick={() => handlePageChange(Math.max(1, pagination.currentPage - 1))}
              disabled={pagination.currentPage === 1}
              className="px-5 py-2.5 border-2 border-gray-300 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gradient-to-r hover:from-orange-50 hover:to-amber-50 hover:border-orange-300 transition-all font-medium shadow-sm hover:shadow-md"
            >
              ← Önceki
            </button>
            <div className="px-6 py-2.5 bg-gradient-to-r from-orange-400 to-amber-500 text-white rounded-xl font-bold shadow-md">
              {pagination.currentPage} / {pagination.totalPages}
            </div>
            <button
              onClick={() => handlePageChange(Math.min(pagination.totalPages, pagination.currentPage + 1))}
              disabled={pagination.currentPage === pagination.totalPages}
              className="px-5 py-2.5 border-2 border-gray-300 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gradient-to-r hover:from-orange-50 hover:to-amber-50 hover:border-orange-300 transition-all font-medium shadow-sm hover:shadow-md"
            >
              Sonraki →
            </button>
          </div>
        )}
      </div>

      {/* Details Modal */}
      {showDetails && selectedUpdate && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="sticky top-0 z-10 bg-gradient-to-r from-orange-500 to-amber-600 px-6 py-5">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-white flex items-center">
                  <Activity className="mr-3" size={28} />
                  Durum Güncellemesi Detayları
                </h2>
                <button
                  onClick={() => setShowDetails(false)}
                  className="text-white/70 hover:text-white hover:bg-white/10 p-2 rounded-xl transition-all"
                >
                  <X size={26} />
                </button>
              </div>
            </div>
            
            <div className="p-6">

              <div className="space-y-6">
                {/* Dog Info */}
                <div className="bg-white rounded-2xl p-6 border-2 border-orange-100 shadow-sm">
                  <div className="flex items-center space-x-5">
                    <div className="w-24 h-24 bg-gradient-to-br from-orange-100 to-amber-100 rounded-2xl flex items-center justify-center ring-4 ring-orange-50 shadow-md">
                      {selectedUpdate.dogId?.profileImage ? (
                        <img 
                          src={selectedUpdate.dogId?.profileImage} 
                          alt={selectedUpdate.dogId?.name || 'Köpek'}
                          className="w-24 h-24 rounded-2xl object-cover"
                        />
                      ) : (
                        <Activity size={36} className="text-orange-400" />
                      )}
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900">{selectedUpdate.dogId?.name || 'Bilinmeyen Köpek'}</h3>
                      <p className="text-gray-600 font-medium mt-1">{selectedUpdate.dogId?.breed || 'Bilinmeyen Irk'}</p>
                      <span className={`inline-flex items-center px-4 py-1.5 text-sm font-bold rounded-full mt-3 ${TYPE_ICONS[selectedUpdate.type].color} shadow-sm`}>
                        {React.createElement(TYPE_ICONS[selectedUpdate.type].icon, { size: 18, className: "mr-2" })}
                        {TYPE_ICONS[selectedUpdate.type].label}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Update Content */}
                <div className="bg-white rounded-2xl p-5 border-2 border-orange-100 shadow-sm">
                  <h4 className="font-bold text-gray-900 mb-3 flex items-center text-lg">
                    <div className="w-8 h-8 bg-gradient-to-br from-orange-400 to-amber-500 rounded-lg flex items-center justify-center mr-3">
                      <MessageCircle size={18} className="text-white" />
                    </div>
                    Güncelleme İçeriği
                  </h4>
                  <div className="space-y-3">
                    <p className="text-gray-700 leading-relaxed">{selectedUpdate.description}</p>
                    <div className="bg-orange-50 rounded-xl p-4 flex items-center justify-between border border-orange-100">
                      <span className="text-sm text-gray-700 font-semibold">Durum:</span>
                      <span className="text-sm font-bold text-orange-700 bg-orange-100 px-4 py-1.5 rounded-lg border border-orange-200">{selectedUpdate.status}</span>
                    </div>
                  </div>
                </div>

                {/* Photo */}
                {selectedUpdate.type === 'photo' && selectedUpdate.photoUrl && (
                  <div className="bg-white rounded-2xl p-5 border-2 border-orange-100 shadow-sm">
                    <h4 className="font-bold text-gray-900 mb-4 flex items-center text-lg">
                      <div className="w-8 h-8 bg-gradient-to-br from-orange-400 to-amber-500 rounded-lg flex items-center justify-center mr-3">
                        <Camera size={18} className="text-white" />
                      </div>
                      Fotoğraf
                    </h4>
                    <div className="space-y-3">
                      <div className="relative inline-block">
                        <img 
                          src={selectedUpdate.photoUrl} 
                          alt="Durum güncellemesi fotoğrafı"
                          className="max-w-xs h-auto rounded-xl shadow-md border-2 border-orange-100"
                        />
                      </div>
                      {selectedUpdate.photoCaption && (
                        <div className="bg-orange-50 p-4 rounded-xl border border-orange-100">
                          <p className="text-sm text-gray-700 font-semibold mb-1">Fotoğraf Açıklaması:</p>
                          <p className="text-sm text-gray-600">{selectedUpdate.photoCaption}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Activity Details */}
                {selectedUpdate.type === 'activity' && selectedUpdate.activityDetails && (
                  <div className="bg-white rounded-2xl p-5 border-2 border-orange-100 shadow-sm">
                    <h4 className="font-bold text-gray-900 mb-4 flex items-center text-lg">
                      <div className="w-8 h-8 bg-gradient-to-br from-orange-400 to-amber-500 rounded-lg flex items-center justify-center mr-3">
                        <Activity size={18} className="text-white" />
                      </div>
                      Aktivite Detayları
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-3">
                        <div className="bg-gray-50 rounded-xl p-3 border border-gray-200">
                          <span className="text-sm font-semibold text-gray-600 block mb-1">Aktivite Türü</span>
                          <span className="text-gray-900 font-medium">
                            {(() => {
                              return selectedUpdate.activityDetails?.activity 
                                ? (ACTIVITY_MAPPING[selectedUpdate.activityDetails.activity.toLowerCase()] || selectedUpdate.activityDetails.activity)
                                : 'Belirtilmemiş';
                            })()}
                          </span>
                        </div>
                        <div className="bg-gray-50 rounded-xl p-3 border border-gray-200">
                          <span className="text-sm font-semibold text-gray-600 block mb-1">Süre</span>
                          <span className="text-gray-900 font-medium">{selectedUpdate.activityDetails.duration || 'Belirtilmemiş'} dakika</span>
                        </div>
                        <div className="bg-gray-50 rounded-xl p-3 border border-gray-200">
                          <span className="text-sm font-semibold text-gray-600 block mb-1">Konum</span>
                          <span className="text-gray-900 font-medium">{selectedUpdate.activityDetails.location || 'Belirtilmemiş'}</span>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <div className="bg-gray-50 rounded-xl p-3 border border-gray-200">
                          <span className="text-sm font-semibold text-gray-600 block mb-1">Ruh Hali</span>
                          <span className="text-gray-900 font-medium">
                            {selectedUpdate.activityDetails.mood 
                              ? (MOOD_MAPPING[selectedUpdate.activityDetails.mood.toLowerCase()] || selectedUpdate.activityDetails.mood)
                              : 'Belirtilmemiş'
                            }
                          </span>
                        </div>
                        <div className="bg-gray-50 rounded-xl p-3 border border-gray-200">
                          <span className="text-sm font-semibold text-gray-600 block mb-2">Enerji Seviyesi</span>
                          <div className="flex items-center">
                            <div className="flex space-x-1">
                              {[1, 2, 3, 4, 5].map((level) => (
                                <div
                                  key={level}
                                  className={`w-5 h-5 rounded-full transition-all ${
                                    level <= (selectedUpdate.activityDetails?.energyLevel || 0)
                                      ? 'bg-gradient-to-br from-orange-400 to-amber-500 shadow-sm'
                                      : 'bg-gray-200'
                                  }`}
                                />
                              ))}
                            </div>
                            <span className="ml-3 text-gray-900 font-bold">
                              {selectedUpdate.activityDetails.energyLevel || 'Belirtilmemiş'}/5
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Health Details */}
                {selectedUpdate.type === 'health' && selectedUpdate.healthDetails && (
                  <div className="bg-white rounded-2xl p-5 border-2 border-orange-100 shadow-sm">
                    <h4 className="font-bold text-gray-900 mb-4 flex items-center text-lg">
                      <div className="w-8 h-8 bg-gradient-to-br from-orange-400 to-amber-500 rounded-lg flex items-center justify-center mr-3">
                        <Heart size={18} className="text-white" />
                      </div>
                      Sağlık Detayları
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-3">
                        <div className="bg-gray-50 rounded-xl p-3 border border-gray-200">
                          <span className="text-sm font-semibold text-gray-600 block mb-1">Sıcaklık</span>
                          <span className={`font-bold text-lg ${
                            (selectedUpdate.healthDetails.temperature || 0) > 39 ? 'text-red-600' : 
                            (selectedUpdate.healthDetails.temperature || 0) < 37 ? 'text-blue-600' : 'text-green-600'
                          }`}>
                            {selectedUpdate.healthDetails.temperature || 'Belirtilmemiş'}°C
                          </span>
                        </div>
                        <div className="bg-gray-50 rounded-xl p-3 border border-gray-200">
                          <span className="text-sm font-semibold text-gray-600 block mb-1">Kilo</span>
                          <span className="text-gray-900 font-medium">{selectedUpdate.healthDetails.weight || 'Belirtilmemiş'} kg</span>
                        </div>
                      </div>
                      <div>
                        <div className="bg-gray-50 rounded-xl p-3 border border-gray-200 h-full">
                          <span className="text-sm font-semibold text-gray-600 block mb-2">Notlar</span>
                          <p className="text-gray-700 leading-relaxed">
                            {selectedUpdate.healthDetails.notes || 'Belirtilmemiş'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Staff and Owner Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white rounded-2xl p-5 border-2 border-orange-100 shadow-sm">
                    <h4 className="font-bold text-gray-900 mb-4 flex items-center">
                      <div className="w-2 h-2 rounded-full bg-gradient-to-br from-orange-400 to-amber-500 mr-2"></div>
                      Sahip Bilgileri
                    </h4>
                    <div className="space-y-3">
                      <ProtectedData
                        label="Sahip Adı"
                        value={selectedUpdate.ownerId?.name || 'Bilinmeyen Kullanıcı'}
                        type="name"
                        pageContext="kopek-durum-guncellemeleri"
                      />
                      <ProtectedData
                        label="E-posta Adresi"
                        value={selectedUpdate.ownerId?.email || 'E-posta bulunamadı'}
                        type="email"
                        pageContext="kopek-durum-guncellemeleri"
                      />
                    </div>
                  </div>

                  <div className="bg-white rounded-2xl p-5 border-2 border-orange-100 shadow-sm">
                    <h4 className="font-bold text-gray-900 mb-4 flex items-center">
                      <div className="w-2 h-2 rounded-full bg-gradient-to-br from-orange-400 to-amber-500 mr-2"></div>
                      Personel Bilgileri
                    </h4>
                    <div className="space-y-2">
                      <div className="bg-gray-50 rounded-xl p-3 border border-gray-200">
                        <span className="text-sm font-semibold text-gray-600 block mb-1">Ad</span>
                        <span className="text-gray-900 font-medium">{selectedUpdate.staffId?.name || 'Bilinmeyen Personel'}</span>
                      </div>
                      <div className="bg-gray-50 rounded-xl p-3 border border-gray-200">
                        <span className="text-sm font-semibold text-gray-600 block mb-1">Rol</span>
                        <span className="text-gray-900 font-medium">{selectedUpdate.staffId?.role || 'Rol bulunamadı'}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Timestamps */}
                <div className="bg-white rounded-2xl p-5 border-2 border-orange-100 shadow-sm">
                  <h4 className="font-bold text-gray-900 mb-4 flex items-center">
                    <div className="w-2 h-2 rounded-full bg-gradient-to-br from-orange-400 to-amber-500 mr-2"></div>
                    Zaman Bilgileri
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 rounded-xl p-3 border border-gray-200">
                      <span className="text-sm font-semibold text-gray-600 block mb-1">Oluşturulma</span>
                      <span className="text-gray-900 font-medium">{formatDate(selectedUpdate.createdAt)}</span>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-3 border border-gray-200">
                      <span className="text-sm font-semibold text-gray-600 block mb-1">Güncellenme</span>
                      <span className="text-gray-900 font-medium">{formatDate(selectedUpdate.updatedAt)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editingUpdate && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] relative shadow-2xl">
            {/* Loading Overlay */}
            {isSaving && (
              <div className="absolute inset-0 bg-white/95 backdrop-blur flex items-center justify-center z-20 rounded-2xl">
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-orange-400 to-amber-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <RefreshCw className="text-white animate-spin" size={32} />
                  </div>
                  <p className="text-gray-900 font-bold text-lg">Kaydediliyor...</p>
                  <p className="text-sm text-gray-600 mt-1">Lütfen bekleyin</p>
                </div>
              </div>
            )}
            <div className={`max-h-[90vh] overflow-y-auto ${isSaving ? 'pointer-events-none' : ''}`}>
            
            <div className="sticky top-0 z-10 bg-gradient-to-r from-orange-500 to-amber-600 px-6 py-5">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-white flex items-center">
                  <Edit className="mr-3" size={28} />
                  Durum Güncellemesini Düzenle
                </h2>
                <button
                  onClick={() => {
                    if (isSaving) return;
                    setShowEditModal(false);
                    setEditingUpdate(null);
                    setNewPhoto(null);
                    setPhotoPreview(null);
                    setRemoveCurrentPhoto(false);
                  }}
                  disabled={isSaving}
                  className={`${isSaving ? 'text-white/30 cursor-not-allowed' : 'text-white/70 hover:text-white hover:bg-white/10 p-2 rounded-xl transition-all'}`}
                >
                  <X size={26} />
                </button>
              </div>
            </div>
            
            <div className="p-6">
              {!editingUpdate ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
                  <p className="text-gray-600 font-medium">Güncelleme bilgileri yükleniyor...</p>
                </div>
              ) : (
                <>

              <div className="space-y-5">
                {/* Status */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center">
                    <span className="w-2 h-2 bg-orange-500 rounded-full mr-2"></span>
                    Durum Başlığı
                  </label>
                  <input
                    type="text"
                    value={editingUpdate.status}
                    onChange={(e) => setEditingUpdate({
                      ...editingUpdate,
                      status: e.target.value
                    })}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-400 focus:border-orange-400 transition-all bg-gray-50 focus:bg-white font-medium"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center">
                    <span className="w-2 h-2 bg-orange-500 rounded-full mr-2"></span>
                    Açıklama
                  </label>
                  <textarea
                    value={editingUpdate.description}
                    onChange={(e) => setEditingUpdate({
                      ...editingUpdate,
                      description: e.target.value
                    })}
                    rows={4}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-400 focus:border-orange-400 transition-all bg-gray-50 focus:bg-white font-medium resize-none"
                  />
                </div>

                {/* Photo Section */}
                {editingUpdate?.type === 'photo' && (
                  <div className="bg-orange-50 rounded-2xl p-5 border-2 border-orange-100">
                    <h4 className="font-bold text-gray-900 mb-4 flex items-center">
                      <div className="w-8 h-8 bg-gradient-to-br from-orange-400 to-amber-500 rounded-lg flex items-center justify-center mr-3">
                        <Camera size={18} className="text-white" />
                      </div>
                      Fotoğraf Yönetimi
                    </h4>
                    <div className="space-y-4">
                    {/* Current Photo */}
                    {editingUpdate.photoUrl && !removeCurrentPhoto && (
                      <div className="bg-white rounded-xl p-4 border border-orange-200">
                        <label className="block text-sm font-semibold text-gray-700 mb-3">
                          Mevcut Fotoğraf
                        </label>
                        <div className="relative inline-block">
                          <img 
                            src={editingUpdate.photoUrl} 
                            alt="Mevcut fotoğraf"
                            className="max-w-xs h-auto rounded-xl shadow-md border-2 border-orange-100"
                          />
                        </div>
                      </div>
                    )}

                    {/* Remove Current Photo Confirmation */}
                    {removeCurrentPhoto && (
                      <div className="p-4 bg-red-50 border-2 border-red-200 rounded-xl">
                        <p className="text-sm text-red-800 font-semibold mb-2">
                          ⚠️ Mevcut fotoğraf kaldırılacak. Bu işlem geri alınamaz.
                        </p>
                        <button
                          type="button"
                          onClick={toggleRemoveCurrentPhoto}
                          className="text-sm text-red-600 hover:text-red-800 font-medium underline"
                        >
                          İptal et
                        </button>
                      </div>
                    )}

                    {/* New Photo Preview */}
                    {photoPreview && (
                      <div className="bg-white rounded-xl p-4 border border-orange-200">
                        <label className="block text-sm font-semibold text-gray-700 mb-3">
                          Yeni Fotoğraf Önizleme
                        </label>
                        <div className="relative inline-block">
                          <img 
                            src={photoPreview} 
                            alt="Yeni fotoğraf önizleme"
                            className="max-w-xs h-auto rounded-xl shadow-md border-2 border-orange-100"
                          />
                          <button
                            type="button"
                            onClick={removeNewPhoto}
                            className="absolute -top-2 -right-2 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-lg font-bold hover:from-red-600 hover:to-red-700 shadow-lg transition-all"
                          >
                            ×
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Photo Upload */}
                    <div className="bg-white rounded-xl p-4 border border-orange-200">
                      <label className="block text-sm font-semibold text-gray-700 mb-3">
                        {editingUpdate.photoUrl ? 'Fotoğrafı Değiştir' : 'Fotoğraf Yükle'}
                      </label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handlePhotoChange}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-400 focus:border-orange-400 transition-all bg-gray-50 focus:bg-white file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-gradient-to-r file:from-orange-400 file:to-amber-500 file:text-white file:font-medium file:cursor-pointer hover:file:from-orange-500 hover:file:to-amber-600"
                      />
                      <p className="text-xs text-gray-600 mt-2 font-medium">
                        JPG, PNG veya GIF formatında fotoğraf seçin
                      </p>
                    </div>

                    {/* Photo Caption */}
                    <div className="bg-white rounded-xl p-4 border border-orange-200">
                      <label className="block text-sm font-semibold text-gray-700 mb-3">
                        Fotoğraf Açıklaması
                      </label>
                      <input
                        type="text"
                        value={editingUpdate.photoCaption || ''}
                        onChange={(e) => setEditingUpdate({
                          ...editingUpdate,
                          photoCaption: e.target.value
                        })}
                        placeholder="Fotoğraf hakkında açıklama..."
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-400 focus:border-orange-400 transition-all bg-gray-50 focus:bg-white font-medium"
                      />
                    </div>
                    </div>
                  </div>
                )}

                {/* Activity Details */}
                {editingUpdate?.type === 'activity' && editingUpdate?.activityDetails && (
                  <div className="bg-orange-50 rounded-2xl p-5 border-2 border-orange-100">
                    <h4 className="font-bold text-gray-900 mb-4 flex items-center">
                      <div className="w-8 h-8 bg-gradient-to-br from-orange-400 to-amber-500 rounded-lg flex items-center justify-center mr-3">
                        <Activity size={18} className="text-white" />
                      </div>
                      Aktivite Detayları
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-white rounded-xl p-3 border border-orange-200">
                        <FormSelect
                          label="Aktivite Türü"
                          value={editingUpdate.activityDetails.activity || ''}
                          onChange={(e) =>
                            setEditingUpdate({
                              ...editingUpdate,
                              activityDetails: {
                                ...editingUpdate.activityDetails,
                                activity: e.target.value
                              }
                            })}
                          className="bg-gray-50"
                        >
                          <option value="">Seçiniz</option>
                          <option value="eating">Yemek</option>
                          <option value="playing">Oyun</option>
                          <option value="sleeping">Uyku</option>
                          <option value="walking">Yürüyüş</option>
                          <option value="training">Eğitim</option>
                          <option value="resting">Dinlenme</option>
                          <option value="socializing">Sosyalleşme</option>
                          <option value="grooming">Temizlik</option>
                          <option value="exercise">Egzersiz</option>
                          <option value="exploring">Keşif</option>
                          <option value="other">Diğer</option>
                        </FormSelect>
                      </div>
                      <div className="bg-white rounded-xl p-3 border border-orange-200">
                        <label className="block text-xs font-semibold text-gray-700 mb-2">Süre (dakika)</label>
                        <input
                          type="number"
                          value={editingUpdate.activityDetails.duration || ''}
                          onChange={(e) => setEditingUpdate({
                            ...editingUpdate,
                            activityDetails: {
                              ...editingUpdate.activityDetails,
                              duration: parseInt(e.target.value) || 0
                            }
                          })}
                          className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-400 transition-all bg-gray-50 focus:bg-white font-medium"
                        />
                      </div>
                      <div className="bg-white rounded-xl p-3 border border-orange-200">
                        <label className="block text-xs font-semibold text-gray-700 mb-2">Konum</label>
                        <input
                          type="text"
                          value={editingUpdate.activityDetails.location || ''}
                          onChange={(e) => setEditingUpdate({
                            ...editingUpdate,
                            activityDetails: {
                              ...editingUpdate.activityDetails,
                              location: e.target.value
                            }
                          })}
                          className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-400 transition-all bg-gray-50 focus:bg-white font-medium"
                        />
                      </div>
                      <div className="bg-white rounded-xl p-3 border border-orange-200">
                        <FormSelect
                          label="Ruh Hali"
                          value={editingUpdate.activityDetails.mood || ''}
                          onChange={(e) =>
                            setEditingUpdate({
                              ...editingUpdate,
                              activityDetails: {
                                ...editingUpdate.activityDetails,
                                mood: e.target.value
                              }
                            })}
                          className="bg-gray-50"
                        >
                          <option value="">Seçiniz</option>
                          <option value="happy">Mutlu</option>
                          <option value="calm">Sakin</option>
                          <option value="excited">Heyecanlı</option>
                          <option value="tired">Yorgun</option>
                          <option value="anxious">Endişeli</option>
                          <option value="playful">Oyunbaz</option>
                          <option value="relaxed">Rahat</option>
                          <option value="energetic">Enerjik</option>
                          <option value="sleepy">Uykulu</option>
                          <option value="alert">Dikkatli</option>
                          <option value="content">Memnun</option>
                          <option value="stressed">Stresli</option>
                          <option value="friendly">Dostane</option>
                          <option value="shy">Utangaç</option>
                          <option value="aggressive">Agresif</option>
                          <option value="neutral">Nötr</option>
                          <option value="other">Diğer</option>
                        </FormSelect>
                      </div>
                      <div className="bg-white rounded-xl p-3 border border-orange-200">
                        <label className="block text-xs font-semibold text-gray-700 mb-2">Enerji Seviyesi (1-5)</label>
                        <input
                          type="number"
                          min="1"
                          max="5"
                          value={editingUpdate.activityDetails.energyLevel || ''}
                          onChange={(e) => setEditingUpdate({
                            ...editingUpdate,
                            activityDetails: {
                              ...editingUpdate.activityDetails,
                              energyLevel: parseInt(e.target.value) || 1
                            }
                          })}
                          className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-400 transition-all bg-gray-50 focus:bg-white font-medium"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Health Details */}
                {editingUpdate?.type === 'health' && editingUpdate?.healthDetails && (
                  <div className="bg-orange-50 rounded-2xl p-5 border-2 border-orange-100">
                    <h4 className="font-bold text-gray-900 mb-4 flex items-center">
                      <div className="w-8 h-8 bg-gradient-to-br from-orange-400 to-amber-500 rounded-lg flex items-center justify-center mr-3">
                        <Heart size={18} className="text-white" />
                      </div>
                      Sağlık Detayları
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-white rounded-xl p-3 border border-orange-200">
                        <label className="block text-xs font-semibold text-gray-700 mb-2">Sıcaklık (°C)</label>
                        <input
                          type="number"
                          step="0.1"
                          value={editingUpdate.healthDetails.temperature || ''}
                          onChange={(e) => setEditingUpdate({
                            ...editingUpdate,
                            healthDetails: {
                              ...editingUpdate.healthDetails,
                              temperature: parseFloat(e.target.value) || 0
                            }
                          })}
                          className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-400 transition-all bg-gray-50 focus:bg-white font-medium"
                        />
                      </div>
                      <div className="bg-white rounded-xl p-3 border border-orange-200">
                        <label className="block text-xs font-semibold text-gray-700 mb-2">Kilo (kg)</label>
                        <input
                          type="number"
                          step="0.1"
                          value={editingUpdate.healthDetails.weight || ''}
                          onChange={(e) => setEditingUpdate({
                            ...editingUpdate,
                            healthDetails: {
                              ...editingUpdate.healthDetails,
                              weight: parseFloat(e.target.value) || 0
                            }
                          })}
                          className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-400 transition-all bg-gray-50 focus:bg-white font-medium"
                        />
                      </div>
                      <div className="col-span-2 bg-white rounded-xl p-3 border border-orange-200">
                        <label className="block text-xs font-semibold text-gray-700 mb-2">Notlar</label>
                        <textarea
                          value={editingUpdate.healthDetails.notes || ''}
                          onChange={(e) => setEditingUpdate({
                            ...editingUpdate,
                            healthDetails: {
                              ...editingUpdate.healthDetails,
                              notes: e.target.value
                            }
                          })}
                          rows={3}
                          className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-400 transition-all bg-gray-50 focus:bg-white font-medium resize-none"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end space-x-3 mt-8 pt-6 border-t-2 border-orange-100">
                <button
                  onClick={() => {
                    if (isSaving) return;
                    setShowEditModal(false);
                    setEditingUpdate(null);
                    setNewPhoto(null);
                    setPhotoPreview(null);
                    setRemoveCurrentPhoto(false);
                  }}
                  disabled={isSaving}
                  className={`px-6 py-3 text-gray-700 bg-white border-2 border-gray-300 rounded-xl transition-all font-bold ${
                    isSaving 
                      ? 'opacity-50 cursor-not-allowed' 
                      : 'hover:bg-gray-50 hover:border-gray-400 hover:shadow-lg'
                  }`}
                >
                  İptal
                </button>
                <button
                  onClick={handleSaveEdit}
                  disabled={isSaving}
                  className={`px-8 py-3 rounded-xl transition-all flex items-center font-bold shadow-lg text-white ${
                    isSaving
                      ? 'bg-orange-300 cursor-not-allowed'
                      : 'bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 hover:shadow-lg'
                  }`}
                >
                  {isSaving ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Kaydediliyor...
                    </>
                  ) : (
                    'Kaydet'
                  )}
                </button>
              </div>
                </>
              )}
            </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-md w-full overflow-hidden shadow-2xl">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-red-500 to-red-600 px-6 py-5">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-white flex items-center">
                  <Trash2 className="mr-3" size={24} />
                  Güncellemeyi Sil
                </h2>
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeletingUpdateId(null);
                  }}
                  className="text-white/70 hover:text-white hover:bg-white/10 p-2 rounded-xl transition-all"
                >
                  <X size={22} />
                </button>
              </div>
            </div>
            
            {/* Modal Body */}
            <div className="p-6">
              <div className="bg-red-50 rounded-2xl p-5 border-2 border-red-200 mb-6">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-gradient-to-br from-red-400 to-red-500 rounded-xl flex items-center justify-center flex-shrink-0 mr-4">
                    <AlertTriangle size={24} className="text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-red-900 mb-2">Dikkat!</h3>
                    <p className="text-red-800 font-medium">
                      Bu durum güncellemesini silmek istediğinizden emin misiniz?
                    </p>
                    <p className="text-red-700 text-sm mt-2">
                      Bu işlem geri alınamaz.
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="flex items-center justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeletingUpdateId(null);
                  }}
                  className="px-6 py-3 text-gray-700 bg-white border-2 border-gray-300 rounded-xl transition-all font-bold hover:bg-gray-50 hover:border-gray-400 hover:shadow-lg"
                >
                  İptal
                </button>
                <button
                  onClick={confirmDelete}
                  className="px-6 py-3 rounded-xl transition-all font-bold shadow-lg text-white bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 hover:shadow-xl"
                >
                  Sil
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KopekDurumGuncellemeleri;
